/**
 * FieldCard - per-block UI for the Notion-style form builder.
 *
 * Each card represents one `FormField`. Visual idiom blends each block into
 * the page rather than wrapping it in a heavy card: no border or fill at
 * rest, only a subtle background tint on hover. The drag grip and per-card
 * `+` sit on an absolutely-positioned rail OUTSIDE the block so the body
 * takes the canvas's full width.
 *
 * Slash command: deliberately NOT wired to the question textarea - that
 * mirrors Notion's actual model where `/` triggers a block-type chooser at
 * the start of an EMPTY block, not inside an existing block's content. The
 * left-rail `+` and the canvas's bottom append button are the only entry
 * points to the field-type menu.
 *
 * Actions (`⋯`) opens a compact menu for editing, duplication, deletion
 * and ordering. Edit reveals the inline settings panel.
 *
 * Shared typed controls edit the question format, answer channel and
 * constraints. Information has at most one attached question, shown as a group.
 *
 * Drag/drop starts only at the grip, preserving native text selection.
 * The card remains a stable drop target while a quiet insertion marker
 * shows the destination. The builder commits placement only on release.
 *
 * Loading state: passing `loading` renders the same outer card chrome with
 * `<Skeleton>` placeholders in place of the editable controls.
 *
 * @module components/form-builder/FieldCard
 */

import { FeatureLesson } from '../learning/FeatureLesson';
import { FieldCardSettingsContext } from './FieldCardSettingsContext';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { useDrag, useDrop, type DragSourceMonitor } from 'react-dnd';
import { IconCopy, IconForms, IconPencil, IconTrash, IconUser, IconX } from '@tabler/icons-react';
import type { QuestionCondition } from '../../lib/question-conditions';
import { BlockActionsMenu } from '../ui/BlockActionsMenu';
import { SparkIcon } from '@/components/icons';
import type { Identifier } from 'dnd-core';
import type {
  CalendarBookingFieldConfig,
  FormField,
  GuestProfileBinding,
  InformationBlockFieldConfig,
} from '../../types';
import { Toggle } from '../ui/Toggle';
import { Skeleton } from '../ui/Skeleton';
import { Tooltip } from '../Tooltip';
import { AddFieldButton, type AddFieldButtonProps } from './AddFieldButton';
import {
  InformationBlockEditor,
  type SurroundingFieldSummary,
} from './InformationBlockEditor';
import { CalendarBlockEditor, CalendarBlockSettings } from './CalendarBlockEditor';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { TypedFieldEditor } from './TypedFieldEditor';
import { QuestionConditionBadge, QuestionConditionEditor } from './QuestionConditionEditor';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { ChoiceOptionsEditor } from './ChoiceOptionsEditor';
import { RatingFieldPreview } from './RatingFieldPreview';
import { AnswerFieldPreview } from './AnswerFieldPreview';
import { ExpandedQuestionEditor, isExpandedQuestionField } from './ExpandedQuestionEditor';
import { FORM_FIELD_CAPABILITIES, getFieldFormat, type FormFieldFormat } from '../../lib/form-capabilities';
import type { FieldType } from '../../types';

/**
 * Information is content, not a question. Its separate Delivery setting
 * controls whether the content is also spoken during a call.
 */
const INFO_BLOCK_TOOLTIP =
  'hollie presents this content without collecting an answer.';

/**
 * Tooltip copy for the read-only "Calendar" chip. Names what the block does at
 * runtime (holito checks a connected calendar and books a follow-up call) so
 * the author understands why it collects no answer.
 */
const CALENDAR_BLOCK_TOOLTIP =
  'holito checks a connected calendar and books a follow-up call during the conversation.';

/** Drag-source/drop-target type discriminator for react-dnd. */
export const FIELD_DND_TYPE = 'holito-form-field';

/** Drag item shape - passed between source and target via react-dnd. */
interface FieldDragItem {
  id: string;
  index: number;
  type: string;
}

/** Props accepted by `FieldCard`. */
export interface FieldCardProps {
  /** Embedded campaigns keep the shared block rail, menu, settings and movement behavior. */
  nested?: { title: string; questionCount: number; showWhen?: QuestionCondition; onCondition: (value?: QuestionCondition) => void };
  /** Whether call-only answer and Information delivery controls are relevant. */
  callsEnabled?: boolean;
  /** Wraps file authoring content in its Drive connection gate. */
  fileDestination?: (content: React.ReactNode) => React.ReactNode;
  /** Inclusive group bounds for keyboard moves that keep an attachment together. */
  groupStartIndex?: number;
  groupEndIndex?: number;
  /** Deleting this Information also deletes its attached question. */
  hasAttachedQuestion?: boolean;
  /** Adds a reportable answer directly after passive Information content. */
  onAddReaction?: (fieldId: string) => void;
  /** The field this card renders. */
  field: FormField;
  /** Earlier authored questions offered as date dependency sources. */
  earlierFields?: readonly FormField[];
  /** Index of the field in the parent FormField[] (drag-reorder identity). */
  index: number;
  /** Called with `(id, patch)` when the admin edits any per-field value. */
  onUpdateField: (id: string, patch: Partial<FormField>) => void;
  /** Called with the field id when the admin clicks the delete button. */
  onDeleteField: (id: string) => void;
  /** Duplicate this block with fresh identities. Calendar cannot be duplicated. */
  onDuplicateField?: (id: string) => void;
  /** Boundary-aware menu ordering, including attached questions. */
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  /** Called with `(dragIndex, hoverIndex)` during drag - mirrors unicorn shape. */
  onMoveField: (dragIndex: number, hoverIndex: number) => number | void;
  /** Optional release-only placement keeps section destinations stable during dragging. */
  onDropField?: (fieldId: string, targetFieldId: string, edge: 'above' | 'below') => void;
  /** Names the owning group on its transient insertion marker. */
  dropLabel?: string;
  /**
   * Called by the per-card "+" rail with `insertIndex = index + 1` so a new
   * field is inserted directly below this one.
   */
  onAddField?: (insertIndex: number, type?: FieldType, format?: FormFieldFormat) => void;
  /** Start a section directly below this card when the host supports grouping. */
  onAddSection?: (insertIndex: number) => void;
  /** Embed another workspace form at this position. */
  onAddNestedForm?: AddFieldButtonProps['onAddNestedForm'];
  /** Generates one question directly below this card. */
  onGenerateField?: (insertIndex: number, description: string, signal: AbortSignal) => Promise<void>;
  /**
   * When true, the card renders a non-interactive skeleton variant in the
   * same outer chrome so the wizard can stream AI-generated fields in
   * without layout shift.
   */
  loading?: boolean;
  /** Visual width hint for the skeleton label. Only read when `loading`. */
  skeletonLabelWidth?: 'short' | 'medium' | 'long';
  /** Owning workspace id, forwarded to an information-block editor. */
  workspaceId?: string;
  /** Optional campaign goal, forwarded to an information-block editor. */
  campaignGoal?: string;
  /** Sibling fields' label+type projection, for caption grounding. */
  surroundingFields?: SurroundingFieldSummary[];
  /**
   * Storage keys of info-block images referenced by the SAVED campaign (edit
   * mode). Forwarded to an information-block editor so removing/replacing a
   * committed image never deletes it from R2.
   */
  committedImageKeys?: ReadonlySet<string>;
  /**
   * When true, the card shows a visual marker flagging an unresolved publish
   * issue (a missing image or a pending caption on an information block). The
   * accompanying `markerHint` populates a `title` attribute.
   */
  hasPublishIssue?: boolean;
  /** Hover hint describing the publish issue. Only read when `hasPublishIssue`. */
  markerHint?: string;
  /**
   * When true, a calendar block already exists in the schema, so this card's
   * "+" rail hides the "Calendar" option (milestone-1 one-per-campaign rule).
   */
  hasCalendarBlock?: boolean;
  /**
   * The question currently saved to each guest property (may be this card).
   * When another question holds this card's property, its toggle becomes a
   * "Use this question" action. Other properties never affect this card.
   */
  guestSources?: GuestBindingSources;
  /**
   * Called with `(fieldId, property, bound)` when the author saves this Email
   * or Name answer to the matching guest property, or clears it. The control
   * is hidden when absent.
   */
  onGuestBindingChange?: (fieldId: string, property: GuestProfileProperty, bound: boolean) => void;
}

/** A guest profile property a question can be saved to: Email to email, Name to name. */
export type GuestProfileProperty = GuestProfileBinding['property'];

/** The question saved to each guest property, by id and label; absent when none is. */
export type GuestBindingSources = Partial<Record<GuestProfileProperty, { id: string; label: string }>>;

/** Shared empty sources so an unwired card keeps a stable default. */
const NO_GUEST_SOURCES: GuestBindingSources = Object.freeze({});

/** Toggle description per guest property. */
const GUEST_BINDING_DESCRIPTIONS: Record<GuestProfileProperty, string> = {
  email: "Use this answer as the guest's email.",
  name: "Use this answer as the guest's name.",
};

/** Relative label widths stay inside the actual question text column. */
const SKELETON_LABEL_WIDTH: Record<
  NonNullable<FieldCardProps['skeletonLabelWidth']>,
  string
> = {
  short: '40%',
  medium: '70%',
  long: '90%',
};

/** Notion-style six-dot grip icon. */
const GripIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 10 16"
    className={className}
    fill="currentColor"
  >
    <circle cx="2" cy="3" r="1.2" />
    <circle cx="2" cy="8" r="1.2" />
    <circle cx="2" cy="13" r="1.2" />
    <circle cx="8" cy="3" r="1.2" />
    <circle cx="8" cy="8" r="1.2" />
    <circle cx="8" cy="13" r="1.2" />
  </svg>
);

/** Three-dot horizontal icon used by the settings trigger. */
const DotsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <circle cx="6" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="18" cy="12" r="1.5" />
  </svg>
);

/**
 * Per-block card. The outer wrapper is `relative + group` so the absolutely
 * positioned left rail (`+`/grip) only appears on hover. The block body has
 * no border or fill at rest - it blends into the canvas, only gaining a
 * subtle background tint on hover.
 */
export const FieldCard: React.FC<FieldCardProps> = ({
  callsEnabled = true,
  fileDestination,
  nested,
  field,
  earlierFields,
  index,
  onUpdateField,
  onDeleteField,
  onDuplicateField,
  canMoveUp = index > 0,
  canMoveDown = false,
  onMoveField,
  onDropField,
  dropLabel,
  onAddField,
  onAddSection,
  onAddNestedForm,
  onGenerateField,
  loading = false,
  skeletonLabelWidth = 'medium',
  workspaceId,
  campaignGoal,
  surroundingFields,
  committedImageKeys,
  hasPublishIssue = false,
  markerHint,
  hasCalendarBlock = false,
  groupStartIndex = index,
  groupEndIndex = index,
  hasAttachedQuestion = false,
  onAddReaction,
  guestSources = NO_GUEST_SOURCES,
  onGuestBindingChange,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const labelRef = useRef<HTMLTextAreaElement | null>(null);
  const settingsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const settingsPanelRef = useRef<HTMLDivElement | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const conditionField = nested ? { type: 'section', showWhen: nested.showWhen } : field;
  const hasCondition = Boolean(conditionField.showWhen);
  const [conditionsOpen, setConditionsOpen] = useState(hasCondition);
  const hadCondition = useRef(hasCondition);
  const [integrationSettingsContainer, setIntegrationSettingsContainer] = useState<HTMLDivElement | null>(null);
  const [dropPosition, setDropPosition] = useState<'above' | 'below' | null>(null);
  const lastDropPosition = useRef<'above' | 'below'>('above');

  /** Open a badge at its first rule, and keep focus in settings after the final rule disappears. */
  useLayoutEffect(() => {
    const removed = hadCondition.current && !hasCondition;
    hadCondition.current = hasCondition;
    if (!settingsOpen) return;
    const selector = removed ? '[data-condition-toggle]' : conditionsOpen ? '[aria-label="Question 1"]' : null;
    if (selector) (settingsPanelRef.current?.querySelector<HTMLElement>(selector)
      ?? settingsPanelRef.current?.querySelector<HTMLElement>('[aria-label="Close settings"]'))?.focus();
  }, [settingsOpen, conditionsOpen, hasCondition]);

  // Email and Name answers each save to their own guest property; other types have none.
  const guestProperty: GuestProfileProperty | null = field.type === 'email' || field.type === 'name' ? field.type : null;
  const guestBound = (field.type === 'email' || field.type === 'name') && field.profileBinding?.property === field.type;
  const guestSource = guestProperty ? guestSources[guestProperty] : undefined;

  /** Auto-grow the label textarea so long question text wraps. */
  const autoSize = useCallback((el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useLayoutEffect(() => {
    if (labelRef.current) autoSize(labelRef.current);
  }, [field.label, autoSize]);

  useLayoutEffect(() => {
    const card = cardRef.current;
    const label = labelRef.current;
    if (!card || !label || typeof ResizeObserver === 'undefined') return undefined;
    let width = card.clientWidth;
    const observer = new ResizeObserver((entries) => {
      const nextWidth = entries[0]?.contentRect.width ?? card.clientWidth;
      if (nextWidth === width) return;
      width = nextWidth;
      autoSize(label);
    });
    observer.observe(card);
    return () => observer.disconnect();
  }, [autoSize, loading]);

  const [{ isDragging, sourceId }, drag, preview] = useDrag(
    () => ({
      type: FIELD_DND_TYPE,
      item: (): FieldDragItem => ({
        id: field.id,
        index,
        type: FIELD_DND_TYPE,
      }),
      collect: (monitor: DragSourceMonitor<FieldDragItem, unknown>) => ({
        isDragging: monitor.isDragging(),
        sourceId: monitor.getHandlerId(),
      }),
    }),
    [field.id, index],
  );

  const [{ isOver, handlerId }, drop] = useDrop<
    FieldDragItem,
    void,
    { handlerId: Identifier | null; isOver: boolean }
  >(
    () => ({
      accept: FIELD_DND_TYPE,
      collect: (monitor) => ({
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver({ shallow: true }),
      }),
      hover(item, monitor) {
        if (!cardRef.current || item.id === field.id) {
          setDropPosition(null);
          return;
        }
        const dragIndex = item.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) {
          setDropPosition(null);
          return;
        }

        // Mirror unicorn-webapp DraggableField.tsx hover-index calc.
        const hoverRect = cardRef.current.getBoundingClientRect();
        const hoverMidY = (hoverRect.bottom - hoverRect.top) / 2;
        const offset = monitor.getClientOffset();
        if (!offset) return;
        const hoverClientY = offset.y - hoverRect.top;

        lastDropPosition.current = hoverClientY < hoverMidY ? 'above' : 'below';
        setDropPosition(lastDropPosition.current);
        if (onDropField) return;

        if (dragIndex < hoverIndex && hoverClientY < hoverMidY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMidY) return;

        try {
          const movedIndex = onMoveField(dragIndex, hoverIndex);
          item.index = movedIndex ?? hoverIndex;
        } catch (error) {
          captureException(error as Error, 'FieldCard.onMoveField', {
            fieldId: field.id,
            dragIndex,
            hoverIndex,
          });
        }
      },
      drop(item, monitor) {
        if (onDropField && item.id !== field.id && cardRef.current) {
          const rect = cardRef.current.getBoundingClientRect();
          const offset = monitor.getClientOffset();
          if (offset) {
            try {
              onDropField(item.id, field.id, offset.y < (rect.top + rect.bottom) / 2 ? 'above' : 'below');
            } catch (error) {
              captureException(error as Error, 'FieldCard.onDropField', { fieldId: field.id, draggedFieldId: item.id });
            }
          }
        }
        setDropPosition(null);
      },
    }),
    [index, field.id, onMoveField, onDropField],
  );

  // Clear the drop indicator the moment the cursor leaves this card OR the
  // drag ends (both flip `isOver` to false). Without this, dropping on a
  // sibling card left the blue indicator stuck on whichever card was last
  // hovered during the drag, since `drop()` only fires on the card the user
  // actually released over.
  useEffect(() => {
    if (!isOver) setDropPosition(null);
  }, [isOver]);

  // Only the grip starts a drag; the editable card remains a drop target.
  const composedRef = useCallback(
    (el: HTMLDivElement | null) => {
      cardRef.current = el;
      drop(el);
      preview(el?.querySelector('textarea') ?? el);
    },
    [drop, preview],
  );

  /** Outside-click for the settings panel. */
  useEffect(() => {
    if (!settingsOpen) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (settingsPanelRef.current?.contains(target)) return;
      if (settingsTriggerRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-condition-summary]') && cardRef.current?.contains(target)) return;
      // Headless UI anchors its options in a portal outside the panel's DOM.
      // Keep only popups owned by this panel inside its disclosure boundary.
      const popup = target instanceof Element ? target.closest('[role="listbox"]') : null;
      if (popup?.id && Array.from(settingsPanelRef.current?.querySelectorAll('[aria-controls]') ?? [])
        .some(control => control.getAttribute('aria-controls')?.split(' ').includes(popup.id))) return;
      setSettingsOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [settingsOpen]);

  /** Keyboard: Alt+Arrow reorders. Enter is suppressed (no newline in label). */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        return;
      }
      if (!e.altKey) return;
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const target = e.key === 'ArrowUp' ? groupStartIndex - 1 : groupEndIndex + 1;
        try {
          onMoveField(index, target);
        } catch (error) {
          captureException(error as Error, 'FieldCard.handleKeyDown', {
            fieldId: field.id,
            from: index,
            to: target,
          });
        }
      }
    },
    [index, field.id, groupStartIndex, groupEndIndex, onMoveField],
  );

  /** Width of the absolute left rail (matches `-left-12` offset = 48px). */
  const LEFT_RAIL_OFFSET = '-left-12';

  const questionContent = (
            <>
              <div className="pr-8 text-base font-semibold leading-snug">
                {loading ? (
                  <Skeleton
                    inline
                    width={SKELETON_LABEL_WIDTH[skeletonLabelWidth]}
                  />
                ) : (
                  <textarea
                    ref={(element) => { labelRef.current = element; if (element) autoSize(element); }}
                    data-testid="field-card-label"
                    rows={1}
                    value={field.label}
                    onChange={(e) => {
                      onUpdateField(field.id, { label: e.target.value });
                      autoSize(e.currentTarget);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a question."
                    className="w-full resize-none overflow-hidden bg-transparent text-base font-semibold leading-snug text-foreground placeholder:text-muted-foreground focus:outline-none"
                  />
                )}
              </div>

              {loading && <div className="mt-2 rounded-lg border border-border/60 bg-background/50 px-3 py-2 text-sm font-normal text-muted-foreground">
                <Skeleton inline width="40%" />
              </div>}
              {/* Choices are edited where their response shape is visible; other formats keep a compact visual preview. */}
              {!loading && (field.type === 'choice_single' || field.type === 'choice_multi') ? (
                <div className="mt-3"><ChoiceOptionsEditor field={field} onChange={(patch) => onUpdateField(field.id, patch)} /></div>
              ) : !loading && isExpandedQuestionField(field) ? (
                <ExpandedQuestionEditor field={field} mode="canvas" workspaceId={workspaceId} committedImageKeys={committedImageKeys}
                  onChange={(patch) => onUpdateField(field.id, patch)} />
              ) : !loading && field.type === 'number' && field.rating ? (
                <div className="mt-3"><RatingFieldPreview field={field} onEdit={() => { setConditionsOpen(hasCondition); setSettingsOpen(true); }} /></div>
              ) : !loading && (field.type === 'email' || field.type === 'phone' || field.type === 'name' || field.type === 'number' || field.type === 'short_text' || field.type === 'free_form_explore' || field.type === 'file_upload') && (
                <AnswerFieldPreview field={field} />
              )}

              {!loading && guestBound && (
                <div className="mt-2">
                  <span
                    data-testid="guest-binding-badge"
                    className="inline-flex items-center gap-1 rounded-lg bg-surface-raised px-2 py-1 text-xs font-medium text-muted-foreground"
                  >
                    <IconUser aria-hidden="true" size={14} />
                    Guest
                  </span>
                </div>
              )}
            </>
  );

  return (
    <FieldCardSettingsContext.Provider value={settingsOpen ? integrationSettingsContainer : null}>
    <div
      ref={loading ? undefined : composedRef}
      data-testid="field-card"
      data-field-id={loading ? undefined : field.id}
      data-field-index={loading ? undefined : index}
      data-drop-target-id={loading ? undefined : handlerId ?? undefined}
      data-field-loading={loading ? 'true' : undefined}
      aria-busy={loading || undefined}
      aria-hidden={loading || undefined}
      className={`group relative ${
        loading
          ? ''
          : `transition-opacity ${isDragging ? 'opacity-40' : 'opacity-100'}`
      }`}
    >
      {!loading && <div className="field-drop-hit-area" aria-hidden="true" />}
      {/* Keep the marker mounted so entry and exit fade without moving the cards. */}
      {!loading && (
        <div data-testid={isOver && dropPosition ? 'field-drop-indicator' : undefined}
          data-placement={dropPosition ?? lastDropPosition.current} data-active={Boolean(isOver && dropPosition)}
          className="field-drop-indicator" role={isOver && dropPosition ? 'status' : undefined}
          aria-hidden={!isOver || !dropPosition}>
          <span className="sr-only">{dropLabel}</span>
        </div>
      )}

      {/* Left rail (+ / drag grip) - absolutely positioned OUTSIDE the block.
          Hover-only on tablet+. Hidden in skeleton mode. */}
      {!loading && (
        <div
          data-testid="field-card-rail"
          className={`absolute ${LEFT_RAIL_OFFSET} top-2 hidden items-start gap-0.5 opacity-0 transition-opacity sm:flex group-hover:opacity-100 group-focus-within:opacity-100`}
        >
          {onAddField && (
            <AddFieldButton
              insertIndex={groupEndIndex + 1}
              onAddField={onAddField}
              onAddSection={onAddSection}
              onAddNestedForm={onAddNestedForm}
              onGenerateField={onGenerateField}
              variant="inline-handle"
              hasCalendarBlock={hasCalendarBlock}
            />
          )}
          <button
            type="button"
            ref={element => { drag(element); }}
            data-testid="field-card-handle"
            data-drag-source-id={sourceId ?? undefined}
            aria-label="Reorder field"
            title="Drag to reorder"
            className="flex h-6 w-6 items-center justify-center rounded-lg text-muted-foreground hover:bg-surface-hover hover:text-foreground cursor-grab active:cursor-grabbing transition-colors"
          >
            <GripIcon className="h-4 w-2.5" />
          </button>
        </div>
      )}

      {/* The block. No fill or border at rest - it blends into the canvas.
          A subtle background tint appears on hover to give the user a
          target for the rail and the settings button. */}
      <div
        className="relative w-full rounded-lg animate-fade-in transition-colors hover:bg-surface-hover/40 group-focus-within:bg-surface/40"
      >
        {/* Actions remain separate from the inline settings editor. */}
        {!loading && (
          <div className="absolute right-1.5 top-1.5 z-10">
            <BlockActionsMenu
              trigger={
                <button ref={settingsTriggerRef} type="button" data-testid="field-card-settings-trigger"
                  aria-label="Block actions" title="Block actions"
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground opacity-100 hover:bg-surface-hover hover:text-foreground sm:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 data-open:opacity-100 focus-visible:ring-2 focus-visible:ring-volt transition cursor-pointer">
                  <DotsIcon className="h-4 w-4" />
                </button>
              }
              items={[
                { label: 'Edit', icon: <IconPencil className="h-4 w-4" />, onClick: () => { setConditionsOpen(hasCondition); setSettingsOpen(true); } },
                { label: 'Duplicate', icon: <IconCopy className="h-4 w-4" />, disabled: field.type === 'calendar_booking' || !onDuplicateField, onClick: () => onDuplicateField?.(field.id) },
                { label: hasAttachedQuestion ? 'Delete both' : 'Delete', icon: <IconTrash className="h-4 w-4" />, className: 'text-danger', onClick: () => onDeleteField(field.id) },
              ]}
              canMoveUp={canMoveUp} canMoveDown={canMoveDown}
              onMoveUp={() => onMoveField(index, groupStartIndex - 1)}
              onMoveDown={() => onMoveField(index, groupEndIndex + 1)}
            />
          </div>
        )}

        <div
          data-testid="field-card-content"
          className="relative px-2 py-2"
        >
          {/* Publish-issue marker: a small dot the publish gate flips on when
              this block is missing its image or its caption is still pending.
              Visual-only; the textual reason lives in the `title` hint. */}
          {!loading && hasPublishIssue && (
            <span
              data-testid="field-card-publish-marker"
              title={markerHint}
              aria-label={markerHint}
              className="absolute right-9 top-2.5 z-10 h-2.5 w-2.5 rounded-full bg-warning"
            />
          )}

          {nested ? <div className="flex items-center gap-3 pr-8">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-border bg-surface-raised text-muted-foreground"><IconForms size={21} stroke={1.6} aria-hidden /></span>
            <div className="min-w-0"><div className="break-words text-base font-semibold leading-snug text-foreground">{nested.title}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">Nested form · {nested.questionCount} {nested.questionCount === 1 ? 'question' : 'questions'}</div></div>
          </div> : !loading && field.type === 'information' && field.contentVariant === 'text' ? (
            <>
              <input
                type="text"
                aria-label="Information block title"
                value={field.title ?? ''}
                placeholder="Title (optional)"
                className="w-full rounded-lg bg-transparent p-2 pr-8 text-base font-semibold text-foreground focus:outline-none"
                onChange={(event) => onUpdateField(field.id, { title: event.target.value })}
                onKeyDown={handleKeyDown}
              />
              <textarea aria-label="Information text" value={field.body ?? ''} rows={3} placeholder="Add Information text." className="w-full resize-y rounded-lg bg-transparent p-2 text-sm text-foreground focus:outline-none" onChange={(event) => onUpdateField(field.id, { body: event.target.value })} onKeyDown={(event) => { if (event.altKey) handleKeyDown(event); }} />
            </>
          ) : !loading && field.type === 'information' ? (
            <InformationBlockEditor
              field={field as FormField & InformationBlockFieldConfig}
              workspaceId={workspaceId}
              campaignGoal={campaignGoal}
              surroundingFields={surroundingFields}
              committedImageKeys={committedImageKeys}
              onUpdateField={(patch) => onUpdateField(field.id, patch)}
            />
          ) : !loading && field.type === 'calendar_booking' ? (
            <CalendarBlockEditor
              field={field as FormField & CalendarBookingFieldConfig}
              onUpdateField={(patch) => onUpdateField(field.id, patch)}
            />
          ) : (
            !loading && field.type === 'file_upload' && fileDestination ? fileDestination(questionContent) : questionContent
          )}

          {!loading && !nested && field.type === 'information' && <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
            <select aria-label="Information format" value={field.contentVariant ?? 'image'} className="cursor-pointer rounded-lg bg-background p-2 text-foreground" onChange={(event) => {
              if (field.imageUrl && !window.confirm('Changing to text removes this image from the question. Continue?')) return;
              onUpdateField(field.id, { contentVariant: event.target.value as 'text' | 'image', responseMode: 'passive' });
            }}><option value="text">Text</option><option value="image">Image</option></select>
            {callsEnabled && <label className="flex items-center gap-2 text-muted-foreground">
              Delivery
              <select value={field.delivery ?? 'spoken'} className="cursor-pointer rounded-lg bg-background p-2 text-foreground" onChange={(event) => onUpdateField(field.id, { delivery: event.target.value as 'spoken' | 'on_screen' })}>
                <option value="spoken">Spoken</option>
                <option value="on_screen">On screen</option>
              </select>
            </label>}
            {onAddReaction && <button type="button" className="cursor-pointer text-muted-foreground hover:text-foreground" onClick={() => onAddReaction(field.id)}>Ask about this</button>}
          </div>}

          {!loading && <QuestionConditionBadge field={conditionField} earlierFields={earlierFields ?? []}
            onEdit={() => { setConditionsOpen(true); setSettingsOpen(true);
              settingsPanelRef.current?.querySelector<HTMLElement>('[aria-label="Question 1"]')?.focus(); }} />}

          {/* Settings panel retains its controls while expanding or retiring. */}
          {!loading && <AnimatedPresence show={settingsOpen}>
            <div
              ref={settingsPanelRef}
              onKeyDown={event => {
                if (event.key === 'Escape') {
                  event.stopPropagation();
                  setSettingsOpen(false);
                  settingsTriggerRef.current?.focus();
                }
              }}
              data-testid="field-card-settings-panel"
              className="mt-3 space-y-4 rounded-xl border border-border bg-background/60 p-4"
            >
              <div className="flex items-center justify-between gap-3">
                {nested || field.type === 'free_form_explore' ? <span data-testid="field-card-type-chip" className="text-sm font-medium text-foreground">{nested ? 'Nested form' : 'Explore'}</span> : <Tooltip
                  content={
                    field.type === 'information'
                      ? INFO_BLOCK_TOOLTIP
                      : field.type === 'calendar_booking'
                        ? CALENDAR_BLOCK_TOOLTIP
                          : field.type === 'file_upload'
                            ? 'Files are saved to your selected Drive folder.'
                            : 'holito collects one answer.'
                  }
                  position="top"
                >
                  <span
                    data-testid="field-card-type-chip"
                    tabIndex={0}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-surface-raised px-2 py-1 text-xs font-medium text-muted-foreground cursor-help focus:outline-none focus:ring-1 focus:ring-volt"
                  >
                    <SparkIcon
                      aria-hidden="true"
                      className="h-3.5 w-3.5"
                    />
                    {field.type === 'file_upload' ? 'File upload' : FORM_FIELD_CAPABILITIES.find((item) => item.format === getFieldFormat(field))?.family}
                  </span>
                </Tooltip>}
                <button type="button" aria-label="Close settings" onClick={() => { setSettingsOpen(false); settingsTriggerRef.current?.focus(); }}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-volt">
                  <IconX aria-hidden="true" className="h-4 w-4" />
                </button>
              </div>

              <QuestionConditionEditor field={conditionField} earlierFields={earlierFields ?? []} open={conditionsOpen} onOpenChange={setConditionsOpen} showSummary={false}
                onChange={showWhen => nested ? nested.onCondition(showWhen) : onUpdateField(field.id, { showWhen })} />

              {!nested && (field.type === 'file_upload' || field.type === 'calendar_booking') && <div ref={setIntegrationSettingsContainer} />}

              {nested || (field.type === 'information' && field.contentVariant === 'text') ? null : field.type === 'information' ? (
                /* Information blocks have no help text / placeholder / required:
                   the only advanced setting is the AI caption (holito's sole
                   surrogate for the image), kept here so the author can review
                   and edit it without it cluttering the visual block. */
                <label className="block">
                  <span className="text-xs font-medium text-muted-foreground">
                    Caption (what holito sees)
                  </span>
                  <textarea
                    data-testid="field-card-info-caption"
                    rows={3}
                    value={(field as FormField & InformationBlockFieldConfig).caption ?? ''}
                    onChange={(e) => onUpdateField(field.id, { caption: e.target.value })}
                    placeholder="A concrete description of the image. holito reasons about it only from this."
                    className="mt-1 w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-volt focus:outline-none"
                  />
                </label>
              ) : field.type === 'calendar_booking' ? (
                /* Host selection is portalled above; all booking constraints
                   share the same disclosed editor and field patch. */
                <CalendarBlockSettings
                  field={field as FormField & CalendarBookingFieldConfig}
                  onUpdateField={(patch) => onUpdateField(field.id, patch)}
                />
              ) : (
                <>
                  <FeatureLesson lesson="explore" ready={field.type === 'free_form_explore'}>
                    <TypedFieldEditor field={field} showOptions={false} allowAnswerChannel={callsEnabled && !isExpandedQuestionField(field)} onChange={(patch) => onUpdateField(field.id, patch)} />
                  </FeatureLesson>
                  {isExpandedQuestionField(field) && <ExpandedQuestionEditor field={field} earlierFields={earlierFields} mode="settings"
                    workspaceId={workspaceId} committedImageKeys={committedImageKeys}
                    onChange={(patch) => onUpdateField(field.id, patch)} />}
                  <label className="block">
                    <span className="text-xs font-medium text-muted-foreground">Help text</span>
                    <input
                      type="text"
                      value={field.helpText ?? ''}
                      onChange={(e) =>
                        onUpdateField(field.id, { helpText: e.target.value })
                      }
                      placeholder="Optional clarification for the respondent."
                      className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-volt focus:outline-none"
                    />
                  </label>

                  {field.type !== 'file_upload' && !isExpandedQuestionField(field) && <label className="block">
                    <span className="text-xs font-medium text-muted-foreground">Placeholder</span>
                    <input
                      type="text"
                      value={field.placeholder ?? ''}
                      onChange={(e) =>
                        onUpdateField(field.id, { placeholder: e.target.value })
                      }
                      placeholder="Optional placeholder text."
                      className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-volt focus:outline-none"
                    />
                  </label>}

                  {guestProperty && onGuestBindingChange && (
                    guestSource && guestSource.id !== field.id ? (
                      <div className="flex items-center justify-between gap-4">
                        <div id={`${field.id}-guest-binding`} className="min-w-0 flex-1">
                          <span className="text-sm font-medium text-foreground">Save to guest</span>
                          <p className="truncate text-xs text-muted-foreground">
                            {`Currently: ${guestSource.label.trim() || 'Untitled question'}`}
                          </p>
                        </div>
                        <button
                          type="button"
                          aria-describedby={`${field.id}-guest-binding`}
                          onClick={() => onGuestBindingChange(field.id, guestProperty, true)}
                          className="shrink-0 cursor-pointer rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-foreground transition hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-volt"
                        >
                          Use this question
                        </button>
                      </div>
                    ) : (
                      <Toggle
                        enabled={guestBound}
                        onChange={(bound) => onGuestBindingChange(field.id, guestProperty, bound)}
                        label="Save to guest"
                        description={GUEST_BINDING_DESCRIPTIONS[guestProperty]}
                        srLabel="Save to guest"
                      />
                    )
                  )}

                  <Toggle
                    enabled={field.required}
                    onChange={(enabled) =>
                      onUpdateField(field.id, { required: enabled })
                    }
                    label="Required"
                    description={field.type === 'file_upload' ? undefined : 'Block the conversation from completing without an answer.'}
                    srLabel="Required"
                  />
                </>
              )}


            </div>
          </AnimatedPresence>}

          {/* Help-text hint when settings panel is closed. */}
          {!loading && !nested && field.helpText && !settingsOpen && (
            <p className="mt-1.5 text-xs text-muted-foreground">{field.helpText}</p>
          )}
        </div>


      </div>
    </div>
    </FieldCardSettingsContext.Provider>
  );
};
